import { useCallback, useRef, useState } from "react";

type UseControlledStateParams<T> = {
  value?: T;
  defaultValue: T;
};

/**
 * Resolves controlled vs uncontrolled state.
 * When `value` is provided, the returned setter is a no-op for internal state
 * (caller must update the controlled prop via their own callback).
 */
export function useControlledState<T>({
  value,
  defaultValue,
}: UseControlledStateParams<T>): [T, (next: T | ((prev: T) => T)) => void] {
  const isControlled = value !== undefined;
  const [uncontrolledValue, setUncontrolledValue] = useState(defaultValue);
  const currentValue = isControlled ? value : uncontrolledValue;
  const valueRef = useRef(currentValue);

  valueRef.current = currentValue;

  const setValue = useCallback((next: T | ((prev: T) => T)) => {
    const resolved = typeof next === "function" ? (next as (prev: T) => T)(valueRef.current) : next;
    if (!isControlled) {
      setUncontrolledValue(resolved);
    }
  }, [isControlled]);

  return [currentValue, setValue];
}
